import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type FormEvent,
  type ReactNode,
} from "react";
import { useNavigate } from "react-router-dom";
import { apiError, useT } from "../i18n";
import { apiJson } from "../lib/api";
import { byName, useProjects } from "../lib/projects";
import { Button, Input, Modal, Select } from "./ui";

/**
 * Neue Tour anlegen — erreichbar aus der Seitenleiste, der Übersicht und einer
 * Projektseite. `presetProjectId` belegt das Projekt vor (Projektseite), sonst
 * entsteht mit dem Tournamen implizit ein neues.
 */
export function NewTourModal({
  open,
  onClose,
  presetProjectId,
}: {
  open: boolean;
  onClose: () => void;
  presetProjectId?: string;
}) {
  const { projects, reload } = useProjects();
  const [name, setName] = useState("");
  const [projectId, setProjectId] = useState(presetProjectId ?? "");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();
  const t = useT();

  // Jedes Öffnen startet leer — sonst steht beim zweiten Mal der alte Name drin
  useEffect(() => {
    if (open) {
      setName("");
      setProjectId(presetProjectId ?? "");
      setError(null);
      setBusy(false);
    }
  }, [open, presetProjectId]);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    try {
      const { id } = await apiJson<{ id: string }>("/api/tours", {
        method: "POST",
        json: { name, projectId: projectId || undefined },
      });
      reload();
      onClose();
      navigate(`/tours/${id}`);
    } catch (err) {
      setError(apiError(t, (err as Error).message));
      setBusy(false);
    }
  }

  return (
    <Modal open={open} onClose={onClose} title={t.dashboard.createTitle}>
      <form onSubmit={(e) => void submit(e)} className="space-y-4">
        <label className="block">
          <span className="mb-1.5 block text-sm font-medium text-zinc-700 dark:text-zinc-300">
            {t.dashboard.tourNameLabel}
          </span>
          <Input
            className="w-full"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={t.dashboard.tourNamePlaceholder}
            required
            autoFocus
          />
        </label>
        <label className="block">
          <span className="mb-1.5 block text-sm font-medium text-zinc-700 dark:text-zinc-300">
            {t.dashboard.projectLabel}
          </span>
          <Select
            className="w-full"
            value={projectId}
            onChange={(e) => setProjectId(e.target.value)}
          >
            <option value="">{t.dashboard.newProjectOption}</option>
            {byName(projects ?? []).map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
          </Select>
        </label>
        {error && <p className="text-sm text-red-600 dark:text-red-400">{error}</p>}
        <div className="flex justify-end gap-2">
          <Button type="button" variant="ghost" onClick={onClose}>
            {t.common.cancel}
          </Button>
          <Button type="submit" disabled={busy}>
            {t.common.create}
          </Button>
        </div>
      </form>
    </Modal>
  );
}

// Der Dialog hängt an EINER Stelle im Rahmen — Seitenleiste, Übersicht und
// Projektseite lösen ihn nur aus. Sonst hätte jede Seite ihre eigene Kopie.

const NewTourContext = createContext<(projectId?: string) => void>(() => {});

export function NewTourProvider({ children }: { children: ReactNode }) {
  const [open, setOpen] = useState(false);
  const [preset, setPreset] = useState<string | undefined>(undefined);

  const start = useCallback((projectId?: string) => {
    setPreset(projectId);
    setOpen(true);
  }, []);

  const value = useMemo(() => start, [start]);

  return (
    <NewTourContext.Provider value={value}>
      {children}
      <NewTourModal open={open} onClose={() => setOpen(false)} presetProjectId={preset} />
    </NewTourContext.Provider>
  );
}

/** Dialog „Neue Tour“ öffnen — optional mit vorbelegtem Projekt. */
export function useNewTour(): (projectId?: string) => void {
  return useContext(NewTourContext);
}
